<template>
    <div class="pt-4 ps-4 pe-4 container">
        <Tabs :vertical="true">
            <Tab label="Sniply" iconClass="sniply-icon icon-dynamic">
                <sniply-invite />
            </Tab>
            <Tab label="Account" iconClass="fa fa-building">
                <form class="form m-3 mt-4">
                    <h4>Account Actions</h4>
                    <hr>
                    <label class="d-block">
                        Accounts
                        <select class="form-control form-select" v-model="selectedAccountId">
                            <option v-for="a in accounts" :key="a.id" :value="a.id">{{ a.name }}</option>
                        </select>
                    </label>
                    <label class="d-block">
                        Account ID
                        <input class="form-control" v-model="accountId"/>
                    </label>
                    <button class="btn btn-primary full-width" disabled>Eventually Clear Cache Someday</button>
                </form>
            </Tab>
            <Tab label="Smart Seller" iconClass="fa fa-users">
                <details open>
                    <summary>Query Parameter Mappings</summary>
                    <form @submit.prevent="handleUpdateParamMappingClick" class="pt-3 px-2">
                        <div class="mb-2">
                            <div class="mb-3">
                                <label for="googleSheetsURL" class="d-block">
                                    Google Sheets URL
                                    <input id="googleSheetsURL" class="form-control"
                                           v-model="paramMappingState.sheetURL"
                                           :readonly="paramMappingState.isUpdating"
                                           type="url"
                                           required
                                           placeholder="https://docs.google.com/spreadsheets/..."
                                    />
                                </label>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col col-8">
                                <div class="alert alert-lightss" role="alert"
                                     v-if="paramMappingState.isUpdating">
                                    <h5 class="alert-heading text-clay-100 font-size-15">Updating ...</h5>
                                </div>
                                <div class="alert alert-info" role="alert"
                                     v-if="!paramMappingState.isUpdating && paramMappingState.updateResult">
                                    <h5 class="alert-heading font-size-18">Update successful</h5>
                                    <p class="mb-1">Following mappings have been updated succesfully.</p>
                                    <hr class="mt-1">
                                    <p class="mb-0 font-monospace"
                                       v-for="title in paramMappingState.updateResult.updated_mappings" :key="title">
                                        {{ title }}
                                    </p>
                                </div>
                                <div class="alert alert-danger" role="alert"
                                     v-else-if="!paramMappingState.isUpdating && paramMappingState.updateError">
                                    <h5 class="alert-heading font-size-18">Update error</h5>
                                    <hr class="mt-1">
                                    <code>
                                        <pre>{{ JSON.stringify(paramMappingState.updateError, null, 2) }}</pre>
                                    </code>
                                </div>
                            </div>
                            <div class="col col-4 text-md-end">
                                <button class="btn btn-primary" :disabled="paramMappingState.isUpdating">Update mappings for<br>"{{currentAccountOverview.name}}"</button>
                            </div>
                        </div>
                    </form>
                </details>
            </Tab>
            <Tab label="App Toggles/Config" iconClass="fa fa-gear">
                <details >
                    <summary>Available Subscription Limits (integers)</summary>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Key</th>
                            <th>Singular</th>
                            <th>Plural</th>
                            <th>Default</th>
                            <th>Per Period</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="x in limitList" :key="x.key">
                            <td>{{ x.key }}</td>
                            <td>{{ x.singular }}</td>
                            <td>{{ x.plural }}</td>
                            <td>{{ x.default }}</td>
                            <td>{{ x.per_period }}</td>
                        </tr>
                        </tbody>
                    </table>
                </details>

                <details>
                    <summary>Available Subscription Features (true|false)</summary>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Key</th>
                            <th>Default</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="x in featureList" :key="x.key">
                            <td>{{ x.key }}</td>
                            <td>{{ x.default }}</td>
                        </tr>
                        </tbody>
                    </table>
                </details>

                <details>
                    <summary>Learn Urls</summary>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Key</th>
                            <th>URL</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="x in learnMoreUrlList" :key="x.key">
                            <td>{{ x.key }}</td>
                            <td>{{ x.url }}</td>
                        </tr>
                        </tbody>
                    </table>
                </details>

                <details>
                    <summary>Feature Toggles</summary>
                    <table class="table">
                        <thead>
                        <tr>
                            <th>Key</th>
                            <th>URL</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="x in featureListCode" :key="x.key">
                            <td>{{ x.key }}</td>
                            <td>{{ x.state }}</td>
                        </tr>
                        </tbody>
                    </table>
                </details>
            </Tab>
            <Tab label="Publishers" iconClass="fa fa-newspaper-o">
                <h4>Publisher Parsing Config</h4>
                <ParsingConfig></ParsingConfig>
            </Tab>
            <Tab label="Import/Export" iconClass="fa fa-file">
                <div class="d-flex flex-wrap">
                    <export-config-file extension="yaml"/>
                    <export-config-file extension="json"/>
                    <import-config-file>

                    </import-config-file>
                </div>
            </Tab>
            <Tab label="Reporting" iconClass="fa fa-pie-chart">
                <details open>
                    <summary>Reporting User Agents</summary>
                    <div class="d-grid gap-2" style="grid-template-columns: 3fr 1fr" >
                        <div style="max-height: calc(100vh - 200px); overflow-y: scroll">
                            <input type="search" class="form-control full-width mb-2 mt-2" v-model="userAgentSearchText">
                            <button type="button" class="btn btn-primary full-width mb-4" @click="loadUserAgents">Load</button>
                            <table class="table">
                                <thead>
                                <tr>
                                    <th>String</th>
                                    <th>Is Bot</th>
                                    <th></th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="ua in userAgents" :key="ua.id">
                                    <td>{{StringUtils.truncate(ua.string, 140)}}</td>
                                    <td><input type="checkbox" readonly :checked="ua.is_bot" class="form-check-input"> </td>
                                    <td>
                                        <button type="button" class="btn btn-link" @click="editUserAgent(ua)">
                                            <i class="fa fa-edit"></i>Edit
                                        </button>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <form @submit.prevent="saveUserAgent" class="mb-4 mt-2">
                            <form-group-card title="Edit User Agent">
                                <div>
                                    <div>
                                        <label class="label-calm full-width">String</label>
                                        <p>{{userAgentString}}</p>
                                    </div>
                                    <div>
                                        <label class="label-calm full-width">ID</label>
                                        <p>{{userAgentId}}</p>
                                    </div>
                                    <label class="form-check form-check-label mb-3">
                                        <input type="checkbox" class="form-check-input" v-model="userAgentIsBot">
                                        Is Bot
                                    </label>
                                    <button type="submit"  class="btn btn-primary full-width">Save</button>
                                </div>
                            </form-group-card>
                        </form>
                    </div>
                </details>
            </Tab>
            <Tab label="Amplify" iconClass="amplify-icon icon-dynamic">
                <amplify-topic-import ref="amplifyTopicModal"/>
                <button class="btn btn-secondary full-width" type="button" @click="$refs.amplifyTopicModal.show">Open Amplify Import</button>
            </Tab>
            <Tab label="Bulk Topics" iconClass="fa fa-plus">
                <BulkTopics/>
            </Tab>
            <Tab label="Add User to Account" iconClass="fa fa-users">
                <AddUserToAccount/>
            </Tab>
        </Tabs>
    </div>
</template>

<script>
import SniplyInvite from "@/components/Integrations/Sniply/SniplyInvite";
import SniplyCampaignAssign from "@/components/Integrations/Sniply/SniplyCampaignAssign";
import {mapGetters} from 'vuex'
import {features} from "@/Features";
import ParsingConfig from "../components/Admin/ParsingConfig";
import ExportConfigFile from "@/components/Admin/ExportConfigFile";
import ImportConfigFile from "@/components/Admin/ImportConfigFile";
import AmplifyTopicImport from "@/components/Admin/AmplifyTopicImport.vue";
import {StringUtils} from "../utilities/Strings";
import FormGroupCard from "@/components/Controls/FormGroupCard.vue";
import Tabs from "@/components/Controls/Tabs/Tabs.vue"
import Tab from "@/components/Controls/Tabs/Tab.vue"
import EasySearch from "@/components/Controls/EasySearch.vue";
import BulkTopics from "@/components/Admin/BulkTopics.vue";
import AddUserToAccount from "@/components/Admin/AddUserToAccount.vue";

export default {
    components: {
        AddUserToAccount,
        BulkTopics,
        EasySearch,
        FormGroupCard,
        Tabs, Tab,
        AmplifyTopicImport,
        ImportConfigFile,
        ExportConfigFile,
        ParsingConfig,
        SniplyCampaignAssign,
        SniplyInvite
    },
    data() {
        return {
            subscriptionFeatures: {},
            subscriptionLimits: {},
            paramMappingState: {
                sheetURL: null,
                isUpdating: false,
                updateResult: null,
                updateError: null
            },
            userAgents: [],
            userAgentsSearched: [],
            userAgentId: '',
            userAgentString: '',
            userAgentIsBot: false,
            userAgentSearchText: '',
            amplifyImportOpen: false,
            selectedAccountId: (this.currentAccountOverview && this.currentAccountOverview.id) || null,
            accountId: (this.currentAccountOverview && this.currentAccountOverview.id) || null
        }
    },
    async mounted() {
        try {
            let result = await this.$api.get('/features/subscription')
            this.subscriptionFeatures = result.data.features
            this.subscriptionLimits = result.data.limits
        } catch (e) {
            console.error('Error fetching subscription details', e)
        }
    },
    methods: {
        async handleUpdateParamMappingClick() {
            if (!confirm(
                    `About to update and reset mappings for account:\n` +
                    `"${this.currentAccountOverview.name}". Continue?`
                )) return
            const { paramMappingState: state } = this
            state.isUpdating = true
            state.updateResult = null
            state.updateError = null
            try {
                let result = await this.$api.post(
                    `/accounts/${this.currentAccountOverview.id}/sniply/param-mappings`,
                    { sheet_url: state.sheetURL }
                )
                state.updateResult = result.data
            } catch (error) {
                console.log('Error updating param mapping', error)
                state.updateError = error
            } finally {
                state.isUpdating = false
            }
        },
        editUserAgent(ua) {
            console.log('ua', ua)
            this.userAgentId = ua.id;
            this.userAgentString = ua.string;
            this.userAgentIsBot = ua.is_bot;
        },
        saveUserAgent() {
            this.$api.put(`/reports/filters/user-agents/${this.userAgentId}/is-bot`, {is_bot: this.userAgentIsBot})
        },
        loadUserAgents() {
            this.$api.get(`/reports/filters/user-agents?search=${this.userAgentSearchText}`).then(r => {this.userAgents = r.data})
        },
        searchUserAgent(search, ua) { console.log(search, ua); return !search || ua.string.toLowerCase().includes(search.toLowerCase()); }
    },
    computed: {
        StringUtils() {
            return StringUtils
        },
        ...mapGetters({
            learnUrls: 'learnUrls',
            accounts: 'allAccounts',
            currentAccountOverview: 'currentAccountOverview',
        }),
        featureList() {
            return Object.entries(this.subscriptionFeatures || {}).map(([key, value]) => ({
                key,
                default: null,
                ...value,
            }))
        },
        limitList() {
            return Object.entries(this.subscriptionLimits || {}).map(([key, value]) => ({
                key,
                singular: '', plural: '', default: null, per_period: null,
                ...value,
            }))
        },
        learnMoreUrlList() {
            return Object.entries(this.learnUrls).map(([key, url]) => ({key, url}))
        },
        featureListCode() {
            return Object.entries(features).map(([key, state]) => ({key, state}))
        }
    },
    watch: {
        selectedAccountId() {
            this.accountId = this.selectedAccountId
        },

        currentAccountOverview() {
            this.selectedAccountId = this.currentAccountOverview && this.currentAccountOverview.id || null;
        }
    }
}
</script>
